import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type ThemeMode = 'light' | 'dark' | 'system';

// ── Reader preferences (readability): a global font SIZE scale + a font FAMILY theme. Applied on <html> so the
// whole rem-based UI scales proportionally (like browser zoom) and every element inherits the family. ──
export type FontScale = 'sm' | 'md' | 'lg' | 'xl';
export type FontFamilyTheme = 'default' | 'system' | 'serif' | 'legible';
/** The page the creator writes on (TYPE-3): today's dark page, or a warm paper page inside the dark workshop. */
export type WritingPage = 'dark' | 'paper';
export const WRITING_PAGE_LABELS: Record<WritingPage, string> = { dark: 'Dark page', paper: 'Paper' };

/** Root font-size per scale — everything is rem-based, so this scales the whole app proportionally. */
export const FONT_SCALES: Record<FontScale, string> = { sm: '93.75%', md: '100%', lg: '112.5%', xl: '125%' };
export const FONT_SCALE_LABELS: Record<FontScale, string> = { sm: 'Small', md: 'Default', lg: 'Large', xl: 'Extra large' };

/** Font-family stacks per theme (all universally available — nothing to bundle). Sets `--brand-font`. */
export const FONT_STACKS: Record<FontFamilyTheme, string> = {
  default: "'Inter', 'Roboto', system-ui, sans-serif",
  system: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  serif: "Georgia, Cambria, 'Times New Roman', serif",
  legible: "Verdana, 'Segoe UI', system-ui, sans-serif",
};
export const FONT_THEME_LABELS: Record<FontFamilyTheme, string> = {
  default: 'Default', system: 'System', serif: 'Reading (serif)', legible: 'Legible',
};

interface ThemeContextType {
  themeMode: ThemeMode;              // User preference
  effectiveTheme: 'light' | 'dark'; // Actual theme applied
  setThemeMode: (mode: ThemeMode) => void;
  fontScale: FontScale;             // Reader font-size preset
  setFontScale: (s: FontScale) => void;
  fontFamily: FontFamilyTheme;      // Reader font-family preset
  setFontFamily: (f: FontFamilyTheme) => void;
  writingPage: WritingPage;         // The page the creator writes on
  setWritingPage: (p: WritingPage) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'wisetree-theme-preference';
const FONT_SCALE_KEY = 'wisetree-font-scale';
const FONT_FAMILY_KEY = 'wisetree-font-family';
const WRITING_PAGE_KEY = 'wisetree-writing-page';

const readFontScale = (): FontScale => {
  const v = localStorage.getItem(FONT_SCALE_KEY) as FontScale;
  return v && v in FONT_SCALES ? v : 'md';
};
const readWritingPage = (): WritingPage => {
  try { return localStorage.getItem(WRITING_PAGE_KEY) === 'paper' ? 'paper' : 'dark'; } catch { return 'dark'; }
};
const readFontFamily = (): FontFamilyTheme => {
  const v = localStorage.getItem(FONT_FAMILY_KEY) as FontFamilyTheme;
  return v && v in FONT_STACKS ? v : 'default';
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const getSystemTheme = (): 'light' | 'dark' => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

/**
 * DARK IS THE PRODUCT'S IDENTITY, not a preference (owner 2026-08-29: *"let's default to dark mode and hide
 * the light/dark toggle for now — keep our identity as dark mode, since that's how it is for cinematography
 * software in general; Higgsfield AI for example, and Kling"*).
 *
 * The reason is craft, not taste: colour work is judged against a dark surround, because a bright chrome
 * shifts how every frame beside it reads. Every tool a creator brings muscle memory from — Premiere, Resolve,
 * Higgsfield, Kling — is dark for that reason. A studio that renders its footage next to a white page is
 * lying to the person grading it.
 *
 * Implemented as a FORCED mode rather than a changed default, so a returning visitor who once chose light
 * does not stay light. The stored preference is left untouched — nothing is destroyed — so restoring the
 * toggle later restores each person's choice with it.
 */
const FORCE_DARK = true;

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    if (FORCE_DARK) return 'dark';
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode;
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      return savedTheme;
    }
    return 'system'; // First visit: pre-select from the user's OS theme
  });

  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(() => {
    if (FORCE_DARK) return 'dark';
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode;
    if (!savedTheme || savedTheme === 'system') {
      return getSystemTheme();
    }
    return savedTheme === 'light' ? 'light' : 'dark';
  });

  // Apply theme to DOM
  useEffect(() => {
    const root = document.documentElement;
    const theme = FORCE_DARK ? 'dark' : themeMode === 'system' ? getSystemTheme() : themeMode;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    setEffectiveTheme(theme);
  }, [themeMode]);

  // Listen for system theme changes when in 'system' mode
  useEffect(() => {
    if (themeMode !== 'system') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const systemTheme = getSystemTheme();
      const root = document.documentElement;

      if (systemTheme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }

      setEffectiveTheme(systemTheme);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [themeMode]);

  const setThemeMode = (mode: ThemeMode) => {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    setThemeModeState(mode);
  };

  // Reader font preferences — applied on <html> (global, inherited). Persist + apply on change.
  const [fontScale, setFontScaleState] = useState<FontScale>(readFontScale);
  const [fontFamily, setFontFamilyState] = useState<FontFamilyTheme>(readFontFamily);

  useEffect(() => { document.documentElement.style.fontSize = FONT_SCALES[fontScale]; }, [fontScale]);
  useEffect(() => { document.documentElement.style.setProperty('--brand-font', FONT_STACKS[fontFamily]); }, [fontFamily]);

  const setFontScale = (s: FontScale) => { localStorage.setItem(FONT_SCALE_KEY, s); setFontScaleState(s); };
  const setFontFamily = (f: FontFamilyTheme) => { localStorage.setItem(FONT_FAMILY_KEY, f); setFontFamilyState(f); };

  // The writing page is a class on <html>; the paper styling lives in index.css under `.writing-paper .ms-page`.
  const [writingPage, setWritingPageState] = useState<WritingPage>(readWritingPage);
  useEffect(() => { document.documentElement.classList.toggle('writing-paper', writingPage === 'paper'); }, [writingPage]);
  const setWritingPage = (p: WritingPage) => {
    try { localStorage.setItem(WRITING_PAGE_KEY, p); } catch { /* not remembered; still applies */ }
    setWritingPageState(p);
  };

  const value: ThemeContextType = {
    themeMode,
    effectiveTheme,
    setThemeMode,
    fontScale,
    setFontScale,
    fontFamily,
    setFontFamily,
    writingPage,
    setWritingPage,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
